import dayjs from "dayjs";

/**
 * Determines if a date should be disabled in the date picker
 * Disables past dates and today if it's past 5 PM
 * @param {dayjs.Dayjs} current - The date to check
 * @returns {boolean} - True if date should be disabled
 */
export const disabledDate = (current) => {
  if (!current) return false;

  const now = dayjs();
  const isPastClosingTime = now.hour() >= 17; // 5pm or later

  // Disable all past dates
  if (current < now.startOf("day")) {
    return true;
  }

  // Disable today if it's past 5pm
  if (isPastClosingTime && current.isSame(now, "day")) {
    return true;
  }

  return false;
};

/**
 * Gets disabled hours and minutes for a specific date
 * Business hours: 8 AM to 5 PM
 * @param {dayjs.Dayjs} currentDate - The selected date
 * @returns {object} - Object with disabledHours and disabledMinutes functions
 */
export const disabledTime = (currentDate) => {
  const now = dayjs();
  const isToday = currentDate && currentDate.isSame(now, "day");

  const currentHour = now.hour();
  const currentMinute = now.minute();

  return {
    disabledHours: () => {
      const hours = [];

      // Disable 12 AM to 7 AM (0-7)
      for (let i = 0; i < 8; i++) hours.push(i);

      // Disable 6 PM to 11 PM (18-23)
      for (let i = 18; i < 24; i++) hours.push(i);

      // If it's today, also disable hours that have already passed
      if (isToday) {
        for (let i = 8; i < currentHour; i++) {
          if (!hours.includes(i)) hours.push(i);
        }
      }

      return hours;
    },

    disabledMinutes: (selectedHour) => {
      // If it's today and the user selects the current hour, disable past minutes
      if (isToday && selectedHour === currentHour) {
        return Array.from({ length: currentMinute }, (_, i) => i);
      }
      return [];
    },
  };
};

/**
 * Gets disabled hours and minutes for the end time picker
 * Ensures end time is after start time and within business hours
 * @param {dayjs.Dayjs} selectedDate - The selected interview date
 * @param {dayjs.Dayjs} startTime - The selected start time
 * @returns {object} - Object with disabledHours and disabledMinutes functions
 */
export const getDisabledEndTime = (selectedDate, startTime) => {
  // Get base constraints (8am-5pm + past time check)
  const baseConstraints = disabledTime(selectedDate);

  if (startTime) {
    const startHour = startTime.hour();
    const startMinute = startTime.minute();

    return {
      disabledHours: () => {
        const baseDisabled = baseConstraints.disabledHours();
        // Disable all hours before start time
        const startConstraint = Array.from({ length: startHour }, (_, i) => i);
        // Combine: Business hours + Past hours + Hours before Start Time
        return Array.from(new Set([...baseDisabled, ...startConstraint]));
      },

      disabledMinutes: (selectedHour) => {
        // If the end hour is the same as the start hour, minutes must be after start minute
        if (selectedHour === startHour) {
          return Array.from({ length: startMinute + 1 }, (_, i) => i);
        }
        // Apply today's past minute constraint if applicable
        return baseConstraints.disabledMinutes(selectedHour);
      },
    };
  }

  return baseConstraints;
};

export const formatDailyLabel = (date) => {
  return dayjs(date).format("MMM D");
};

export const formatWeeklyLabel = (from, to) => {
  const fromDate = dayjs(from);
  const toDate = dayjs(to);
  const fromMonth = fromDate.format("MMM");
  const toMonth = toDate.format("MMM");
  const year = toDate.year();

  if (fromMonth === toMonth) {
    return `${fromMonth} ${fromDate.date()}-${toDate.date()} ${year}`;
  }
  return `${fromMonth} ${fromDate.date()} - ${toMonth} ${toDate.date()} ${year}`;
};
